// Protected route wrapper with permission-based access control
import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Permission, ROUTE_PERMISSIONS } from '../types/user.types';
import { UI_THEME } from '../ui/theme';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: Permission[];
  requiredRoles?: string[];
}

const AccessDenied: React.FC = () => {
  const navigate = useNavigate();
  const buttonClass = UI_THEME === 'saas'
    ? 'bg-teal-600 hover:bg-teal-700 focus-visible:outline-teal-600 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 hover:bg-blue-800 focus-visible:outline-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500';

  return (
    <div className="flex min-h-[55vh] items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <svg className="mx-auto mb-4 h-12 w-12 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.7 2.6 17a2 2 0 0 0 1.73 3h15.34a2 2 0 0 0 1.73-3L13.7 3.7a2 2 0 0 0-3.4 0Z" />
        </svg>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Acesso negado</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Seu perfil não possui acesso a esta funcionalidade.
        </p>
        <button
          type="button"
          onClick={() => navigate('/dashboard', { replace: true })}
          className={`mt-6 rounded-lg px-4 py-2 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${buttonClass}`}
        >
          Voltar para a área de trabalho
        </button>
      </div>
    </div>
  );
};

const PermissionsUnavailable: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const buttonClass = UI_THEME === 'saas'
    ? 'bg-teal-600 hover:bg-teal-700 focus-visible:outline-teal-600 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 hover:bg-blue-800 focus-visible:outline-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500';

  return (
    <div className="flex min-h-[55vh] items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <svg className="mx-auto mb-4 h-12 w-12 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 1 1-2.64-6.36M21 4v6h-6" />
        </svg>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Não foi possível carregar suas permissões</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Verifique sua conexão e tente novamente.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className={`mt-6 rounded-lg px-4 py-2 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${buttonClass}`}
        >
          Tentar novamente
        </button>
      </div>
    </div>
  );
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredPermissions, requiredRoles }) => {
  const { isAuthenticated, hasAnyPermission, user, permissionsLoading, permissionsError, retryLoadPermissions } = useAuth();
  const location = useLocation();
  const hasStoredToken = typeof window !== 'undefined' && Boolean(sessionStorage.getItem('token'));

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // In a fresh tab, auth state can be restored from localStorage before the user object is
  // hydrated, and permissions always load asynchronously from the backend. Wait for both
  // instead of deciding access on an empty/guessed permission list.
  if (isAuthenticated && hasStoredToken && (!user || permissionsLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="rounded-lg bg-white px-6 py-4 text-sm text-gray-600 shadow-sm">
          Carregando permissões...
        </div>
      </div>
    );
  }

  // The backend is the only source of truth for permissions — if fetching them failed,
  // never fall back to a guess. Block access and let the user retry.
  if (permissionsError) {
    return <PermissionsUnavailable onRetry={retryLoadPermissions} />;
  }

  // Role-based guard
  if (requiredRoles && requiredRoles.length > 0) {
    const userRole = String(user?.role ?? '').toUpperCase();
    if (!requiredRoles.map(r => r.toUpperCase()).includes(userRole)) {
      return <AccessDenied />;
    }
  }

  // Check route-based permissions if defined
  const routePermissions = ROUTE_PERMISSIONS[location.pathname] || requiredPermissions;

  if (routePermissions && routePermissions.length > 0) {
    if (!hasAnyPermission(routePermissions)) {
      return <AccessDenied />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;

